import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
interface SoundContextType {
  effectsEnabled: boolean;
  setEffectsEnabled: (enabled: boolean) => void;
  playSoundIfEnabled: (src: string) => Promise<void>;
}
const SoundContext = createContext<SoundContextType | undefined>(undefined);
export const useSoundContext = () => {
  const context = useContext(SoundContext);
  if (context === undefined) {
    throw new Error('useSoundContext must be used within a SoundProvider');
  }
  return context;
};
interface SoundProviderProps {
  children: React.ReactNode;
  initialEffectsEnabled?: boolean;
}
export const SoundProvider: React.FC<SoundProviderProps> = ({
  children,
  initialEffectsEnabled = true
}) => {
  const [effectsEnabled, setEffectsEnabled] = useState(initialEffectsEnabled);
  const playSoundIfEnabled = useCallback(async (src: string): Promise<void> => {
    if (!effectsEnabled) {
      return;
    }
    if (typeof Audio === 'undefined') return;
    try {
      const audio = new Audio(src);
      audio.preload = 'auto';
      await audio.play();
    } catch (error) {
      // Re-throw with original error information for debugging
      throw new Error(`Audio playback failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [effectsEnabled]);
  const contextValue = useMemo(() => ({
    effectsEnabled,
    setEffectsEnabled,
    playSoundIfEnabled
  }), [effectsEnabled, playSoundIfEnabled]);
  return <SoundContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="SoundProvider" data-sentry-source-file="sound-context.tsx">{children}</SoundContext.Provider>;
};